import { Box, Tooltip } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_SortingState,
  MaterialReactTable,
} from "material-react-table";
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import "@/components/files/documents.css";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { useTranslation } from "react-i18next";
import { IFileItem } from "@/types/FileItem";
import FileTableIcon from "@/components/files/fileTableIcon";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useDispatch, useSelector } from "react-redux";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import styles from "@/components/tables/simpleTableViews/documentTable.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { RootState } from "@/components/state/store";
import AppointmentTreeItemUploadButton from "@/components/appointmentDashboard/appointmentTreeItemUploadButton";
import { replaceDate } from "@/helper/dateHandling";
import {
  buildSimpleMrtColumnFilterApiParams,
  hasIncompleteMrtDateFilter,
} from "@/components/tables/genericCrudTableDateFilterIntegration";
import { normalizeCustomFilterColumns } from "@/components/tables/genericCrudTableFilterColumnIntegration";
import MrtToolbarInternalActions from "@/components/tables/mrtToolbarInternalActions";
import {
  hasMrtFiltersToReset,
  resetMrtFilters,
} from "@/components/tables/mrtTableReset.utils";

const initialDocumentSorting: MRT_SortingState = [
  { id: "created_on", desc: true },
];

interface Props {
  subjectId: any;
  folderId?: any;
  subjectForDispatch?: any;
  openWithSpecificSubjectIdUrl?: boolean;
  folder: "all" | "reports" | "uploads";
  additionalColumns?: any;
  customPageSize?: any;
  customDensity?: "spacious" | "compact" | "comfortable";
  customTopToolBarAction?: ReactNode | undefined;
  tableMargin?: any;
  tableBoxShadow?: any;
  buttonLinkToDocuments?: boolean;
  showAppointmentDocumentUploadButton?: boolean;
  appointmentIdForUpload?: string;
  handleTriggerReload?: () => void;
}

const DocumentTable: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const [fileData, setFileData] = useState<IFileItem[]>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilter, setColumnFilter] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [isRefetching, setIsRefetching] = useState(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: props.customPageSize ? props.customPageSize : 20,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>(
    initialDocumentSorting
  );
  const preservePaginationOnFilterResetRef = useRef(false);
  const { handleError, errorModal } = useErrorModal();

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const columnSorting = "-modified_on";

  const reportsFolderId = "00000026-1111-0000-0000-000000000000";
  const uploadFolderId = "00000026-1111-0000-0000-000000000001";

  const dispatch = useDispatch();

  const logedInUserInfo: any = useSelector((state: RootState) => state.login);

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    const baseColumns: MRT_ColumnDef<any>[] = [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
        enableHiding: false,
      },

      {
        header: t("name"),
        accessorKey: "name",
        id: "name",
        Cell: ({ row, renderedCellValue }: any) => {
          return (
            <Box className={styles.fileTableName}>
              <FileTableIcon extension={row.getValue("extension")} />
              <a
                href={"/api/v1" + row.original.view_link}
                target="_blank"
                rel="noreferrer"
                className="fileTableNameLink"
              >
                <Tooltip title={formatFilenameString(renderedCellValue)}>
                  <span>{formatFilenameString(renderedCellValue)}</span>
                </Tooltip>
              </a>
            </Box>
          );
        },
      },

      {
        header: t("extension"),
        accessorKey: "extension",
        id: "extension",
        size: 40,
      },
      {
        header: t("created_on"),
        accessorKey: "created_on",
        id: "created_on",
        size: 80,
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue(), true, true)}</span>;
        },
      },
      {
        header: t("modified_on"),
        accessorKey: "modified_on",
        id: "modified_on",
        filterVariant: "date",

        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue(), true, true)}</span>;
        },
      },
    ];

    if (props.additionalColumns) {
      baseColumns.push(props.additionalColumns);
    }

    return normalizeCustomFilterColumns(baseColumns);
  }, [props.additionalColumns, t]);

  const tableOptions = {
    columnHiding:
      subdomain === "admin" || subdomain === "manage" ? true : false,
  };

  useEffect(() => {
    if (!initialLoading) {
      {
        const fetchData = async () => {
          if (fileData.length !== 0) {
            setIsRefetching(true);
          }
          const additionalUrlParameters: any = {};
          if (props.folder === "reports") {
            additionalUrlParameters["folder_id"] = reportsFolderId;
          }
          if (props.folder === "uploads") {
            additionalUrlParameters["folder_id"] = uploadFolderId;
          }

          if (props.subjectId) {
            additionalUrlParameters["impersonate_subject"] = props.subjectId;
          }

          if (columnFilter.length !== 0) {
            if (hasIncompleteMrtDateFilter(columnFilter, columns)) {
              setIsRefetching(false);
              return;
            }
            Object.assign(
              additionalUrlParameters,
              buildSimpleMrtColumnFilterApiParams(columnFilter, columns)
            );
          }

          try {
            /*eslint-disable */
            const res = await api.genericApiRequest({
              entity: "file",
              method: "get",
              parametersToRender: {
                depth: "0",
                ordering: columnSorting,
                limit: pagination.pageSize,
                offset: pagination.pageIndex * pagination.pageSize,
                search: globalFilter,
                additionalUrlParameters: additionalUrlParameters,
              },

              successHandler: (res: any) => {
                /*eslint-enable */
                const data = res.data;
                setFileData(data.results);
                setRowCount(data.count);
                setInitialLoading(true);
                const files = [];
                for (const key in data.results) {
                  const item = data.results[key];
                  const id = item.id;
                  const name = item.name;
                  files.push({ id: id, name: name });
                }
              },
            });
          } catch (error) {
            handleError(error);
            return;
          }
          setIsRefetching(false);
        };
        fetchData();
      }
    }
  }, [
    props.folderId,
    props.subjectId,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    columnFilter,
    columns,
    initialLoading,
    columnSorting,
  ]);

  function formatFilenameString(fileName: any) {
    let validFileName;

    if (typeof fileName === "object") {
      validFileName = fileName.props?.["aria-label"];
    } else if (typeof fileName === "string") {
      validFileName = fileName;
    }
    let stringWithSpaces = validFileName.replace(/_+/g, " ");

    const dateRegex = /(\d{2,4}-\d{1,2}-\d{1,2})$/;
    const match = stringWithSpaces.match(dateRegex);

    if (match) {
      const dateString = match[1];
      const [year, month, day] = dateString.split("-");
      const formatedDate = `${day}.${month}.${year}`;
      stringWithSpaces = stringWithSpaces.replace(dateRegex, formatedDate);
    }
    return stringWithSpaces;
  }

  const columnVisibility = {
    id: false,
    created_on: true,
    extension: false,
    modified_on: false,
  };

  const handleNavigateToDocuments = () => {
    if (props.openWithSpecificSubjectIdUrl && props.subjectId) {
      window.open(
        `/document/${props.subjectId}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    const subject = {
      value: props?.subjectForDispatch?.id,
      label: props?.subjectForDispatch?.name,
    };
    dispatch(setSubjectSearch(subject));
    window.open("/document", "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <MaterialReactTable
        localization={
          logedInUserInfo.preferred_language === "en"
            ? MRT_Localization_EN
            : MRT_Localization_DE
        }
        columns={columns}
        data={fileData}
        enableHiding={tableOptions.columnHiding}
        enableFullScreenToggle={false}
        positionToolbarAlertBanner="none"
        displayColumnDefOptions={{
          "mrt-row-actions": {
            size: 40,
            muiTableHeadCellProps: {
              align: "center",
            },
          },
        }}
        muiTablePaperProps={{
          sx: {
            width: "100%",
            boxShadow: props.tableBoxShadow && props.tableBoxShadow,
            marginTop: props.tableMargin ? props.tableMargin : "1rem",
            "&&  .MuiCollapse-root, && .MuiCollapse-wrapperInner": {
              width: "90%",
            },
            "& .MuiAlert-root": { backgroundColor: "#fff" },
            "& .MuiInputBase-root, && .MuiInput-root::before, && .MuiInput-root::after":
              { borderBottom: "none" },
            "& .MuiTextField-root": {
              backgroundColor: "#f4f2f2",

              outline: "none",
            },
            "&& .MuiBox-root": {
              alignItems: "center",
              justifyContent: "space-arround",
            },
          },
        }}
        muiTableBodyRowProps={({ row }: any) =>
          row.index % 2 === 0
            ? { sx: { backgroundColor: "#f5f5f5" } }
            : { sx: { backgroundColor: "#FFF" } }
        }
        muiTableBodyCellProps={{
          sx: {
            borderRight: "1px solid #e0e0e0",
          },
        }}
        initialState={{
          columnVisibility: columnVisibility,
          density: props.customDensity ? props.customDensity : "spacious",
          showGlobalFilter: true,
          showColumnFilters: false,
          sorting: initialDocumentSorting,
        }}
        state={{
          pagination: pagination,
          globalFilter: globalFilter,
          columnFilters: columnFilter,
          showProgressBars: isRefetching,
          sorting: sorting,
        }}
        positionGlobalFilter="left"
        muiSearchTextFieldProps={{
          onClick: () => {
            setGlobalFilter("");
          },
        }}
        onGlobalFilterChange={(e: any) => {
          setGlobalFilter(e);
          setInitialLoading(false);
        }}
        enableColumnFilters={false}
        onColumnFiltersChange={(filters) => {
          setColumnFilter(filters);
          if (preservePaginationOnFilterResetRef.current) {
            preservePaginationOnFilterResetRef.current = false;
          } else {
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }
          setInitialLoading(false);
        }}
        onSortingChange={(e) => {
          setSorting(e);
        }}
        renderToolbarInternalActions={({ table }) => (
          <MrtToolbarInternalActions
            table={table}
            showReset={hasMrtFiltersToReset(
              table.getState(),
              initialDocumentSorting,
            )}
            onReset={() => {
              preservePaginationOnFilterResetRef.current = true;
              resetMrtFilters(table, initialDocumentSorting);
            }}
          />
        )}
        manualPagination={true}
        onPaginationChange={(e: any) => {
          setPagination(e);
          setInitialLoading(false);
        }}
        manualFiltering={true}
        rowCount={rowCount}
        enableRowSelection={false}
        renderTopToolbarCustomActions={() => {
          let toolBarItems: ReactNode = <></>;

          toolBarItems = (
            <>
              {props.showAppointmentDocumentUploadButton &&
                props.subjectForDispatch && (
                  <div className={styles.uploadButton}>
                    <AppointmentTreeItemUploadButton
                      useCase="documentWidget"
                      renderDocumentCaseAsButton={true}
                      renderDocumentCaseAsButtonWithTooltip={true}
                      subject={props.subjectForDispatch}
                      appointmentId={props.appointmentIdForUpload}
                      handleTriggerReload={props.handleTriggerReload}
                    />
                  </div>
                )}
              {props.buttonLinkToDocuments && (
                <GenericButton
                  startIcon={<OpenInNewIcon />}
                  onClick={handleNavigateToDocuments}
                >
                  {t("documents")}
                </GenericButton>
              )}
              {props.customTopToolBarAction && props.customTopToolBarAction}
            </>
          );
          return (
            <div className={styles.toolBarItemsContainer}>{toolBarItems}</div>
          );
        }}
        enableRowActions={false}
      />
      {errorModal}
    </>
  );
};

export default DocumentTable;
